import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { ImageField, Field } from "./ImageField";

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-steel/20 bg-aero-surface p-5 sm:p-6">
    <h2 className="mb-4 font-heading text-sm font-bold uppercase tracking-[0.18em] text-saffron">{title}</h2>
    <div className="space-y-4">{children}</div>
  </div>
);

export const HomeTab = () => {
  const [home, setHome] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/content/home")
      .then((r) => setHome(r.data))
      .catch((e) => toast.error(apiErr(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/home", home);
      toast.success("Home page updated — changes are live");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!home) return <Loader2 className="mx-auto mt-20 h-6 w-6 animate-spin text-saffron" />;

  const setHero = (k, v) => setHome({ ...home, hero: { ...home.hero, [k]: v } });
  const setCommunity = (k, v) => setHome({ ...home, community: { ...home.community, [k]: v } });
  const setStat = (i, k, v) =>
    setHome({ ...home, stats: home.stats.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) });
  const setCard = (i, k, v) =>
    setHome({ ...home, whatWeBuild: home.whatWeBuild.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) });

  return (
    <div data-testid="admin-home">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-ink">Home Page Content</h1>
          <p className="mt-1 font-body text-sm text-steel">Edit the hero, stats, product cards, and community section.</p>
        </div>
        <button
          data-testid="home-save-btn"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-saffron px-6 py-2.5 font-heading text-sm font-semibold text-white transition-colors hover:bg-saffron-soft disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save All Changes
        </button>
      </div>

      <div className="space-y-5">
        <Section title="Hero Section">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Overline (small badge)" value={home.hero.overline} testId="hero-field-overline" onChange={(v) => setHero("overline", v)} />
            <ImageField label="Hero photo" value={home.hero.image} testId="hero-image" onChange={(v) => setHero("image", v)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {(home.hero.headlineLines || []).map((ln, i) => (
              <Field
                key={i}
                label={`Headline — line ${i + 1}`}
                value={ln}
                testId={`hero-field-line${i + 1}`}
                onChange={(v) => {
                  const lines = [...home.hero.headlineLines];
                  lines[i] = v;
                  setHero("headlineLines", lines);
                }}
              />
            ))}
          </div>
          <Field label="Intro paragraph" textarea value={home.hero.intro} testId="hero-field-intro" onChange={(v) => setHero("intro", v)} />
        </Section>

        <Section title="Stats Strip">
          {home.stats.map((s, i) => (
            <div key={i} className="grid gap-3 sm:grid-cols-[110px_90px_1fr]">
              <Field label={`Value ${i + 1}`} type="number" value={s.value} testId={`stat-value-${i}`} onChange={(v) => setStat(i, "value", Number(v))} />
              <Field label="Suffix" value={s.suffix} testId={`stat-suffix-${i}`} onChange={(v) => setStat(i, "suffix", v)} />
              <Field label="Label" value={s.label} testId={`stat-label-${i}`} onChange={(v) => setStat(i, "label", v)} />
            </div>
          ))}
        </Section>

        <Section title="What We Build — Product Cards">
          {home.whatWeBuild.map((c, i) => (
            <div key={c.id || i} className="rounded-lg border border-steel/15 bg-white/60 p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={`Card ${i + 1} — Title`} value={c.title} testId={`card-title-${i}`} onChange={(v) => setCard(i, "title", v)} />
                <ImageField label="Card photo" value={c.bg} testId={`card-image-${i}`} onChange={(v) => setCard(i, "bg", v)} />
              </div>
              <div className="mt-3">
                <Field label="Blurb" textarea value={c.blurb} testId={`card-blurb-${i}`} onChange={(v) => setCard(i, "blurb", v)} />
              </div>
            </div>
          ))}
        </Section>

        <Section title="Community Section">
          <Field label="Heading" value={home.community.heading} testId="community-field-heading" onChange={(v) => setCommunity("heading", v)} />
          <Field label="Body" textarea value={home.community.body} testId="community-field-body" onChange={(v) => setCommunity("body", v)} />
          <Field
            label="Points (one per line)"
            textarea
            value={(home.community.points || []).join("\n")}
            testId="community-field-points"
            onChange={(v) => setCommunity("points", v.split("\n"))}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Badge title" value={home.community.badgeTitle} testId="community-field-badge-title" onChange={(v) => setCommunity("badgeTitle", v)} />
            <Field label="Badge text" value={home.community.badgeText} testId="community-field-badge-text" onChange={(v) => setCommunity("badgeText", v)} />
          </div>
          <ImageField label="Community photo" value={home.community.image} testId="community-image" onChange={(v) => setCommunity("image", v)} />
        </Section>

        <button
          data-testid="home-save-btn-bottom"
          onClick={save}
          disabled={saving}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-saffron py-3 font-heading text-sm font-semibold text-white transition-colors hover:bg-saffron-soft disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save All Changes
        </button>
      </div>
    </div>
  );
};
